/* eslint-disable camelcase */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import localeMessage from './localeMessage';
import Close from '../../../../images/sessions/close.svg';
import { submitSessionFeedback } from '../../../actions/sessions';
import RadioButton from './VerticalRadioButton';
import { THERAPY_URL } from '../../../environment';

const options = [
  { id: 'technicalIssues', value: 'Technical issues' },
  { id: 'forMe', value: "Counselling isn't for me" },
  {
    id: 'notConnect',
    value: 'I didn’t connect with my care provider',
  },
  {
    id: 'notShow',
    value: "My care provider was very late/didn't show up",
  },
  {
    id: 'wasUnprofessional',
    value: 'My care provider was unprofessional',
  },
  {
    id: 'other',
    value: 'Other',
  },
];

const successOptions = [
  { id: 'successfulSession', value: 'Successful: my session had no issues' },
];

function CompleteSessionDialog({
  appointment = {},
  onClose = () => {},
  open = false,
  submitSessionFeedback,
  user = {},
  locale,
}) {
  const [textAreaValue, setTextAreaValue] = useState('');
  const [requiredError, setRequiredError] = useState(false);

  const { handleSubmit, control, watch, reset } = useForm({
    defaultValues: {
      feedback: null,
      successFeedback: null,
    },
  });

  const feedbackValue = watch('feedback');
  const successFeedbackValue = watch('successFeedback');

  const handleRadioChange = fieldName => {
    setRequiredError(false);
    // Clear the opposite radio group when one is selected
    reset({
      feedback: fieldName === 'feedback' ? watch('feedback') : null,
      successFeedback:
        fieldName === 'successFeedback' ? watch('successFeedback') : null,
    });
  };

  const submitFeedback = (feedback, status) => {
    const { id, counselling_type } = appointment;

    if (textAreaValue) {
      feedback = feedback ? `${feedback}, ${textAreaValue}` : textAreaValue;
    }

    const params = {
      id,
      feedback,
      status,
    };

    submitSessionFeedback(params, () => {
      if (params.status === 'successful') {
        if (counselling_type === 'consultation') {
          window.location.href = `${THERAPY_URL}/sessions/booking?status=successful`;
        } else {
          window.location.href = `${THERAPY_URL}/sessions/${id}/post_session_assessments`;
        }
      } else if (params.feedback?.startsWith("Counselling isn't for me")) {
        window.location.href = `${THERAPY_URL}`;
      } else {
        window.location.href = `${THERAPY_URL}/sessions/unsuccessful`;
      }
    });
  };

  const onSubmit = values => {
    if (values.feedback) {
      if (values.feedback === 'Other' && !textAreaValue.trim()) {
        setRequiredError(true);
      } else {
        submitFeedback(values.feedback, 'unsuccessful');
      }
    } else if (values.successFeedback) {
      submitFeedback(null, 'successful');
    }
  };

  const completeCouplesGuestSession = () => {
    window.location.href = `${THERAPY_URL}/`;
  };

  const localeOptions = () =>
    options.map(option => ({
      label: localeMessage(option.id, locale),
      value: option.value,
    }));

  const successLocaleOptions = () =>
    successOptions.map(option => ({
      label: <b>{localeMessage(option.id, locale)}</b>,
      value: option.value,
    }));

  if (!open) return <div />;

  // Couples guest flow
  if (
    appointment.provider_type === 'couples' &&
    user.id === appointment.couples_appointment_detail?.guest?.id
  ) {
    return (
      <div className="csd-container">
        <div className="csd-overlay" />
        <div className="csd-body">
          <div className="csd-close">
            <Close onClick={onClose} />
          </div>
          <h2>{localeMessage('toComplete', locale)}</h2>
          <button
            className="green-button"
            onClick={completeCouplesGuestSession}
            style={{ marginRight: 30 }}
            type="button"
          >
            {localeMessage('yes', locale)}
          </button>
          <button className="white-button" onClick={onClose} type="button">
            {localeMessage('no', locale)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="csd-container">
      <div className="csd-overlay" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="csd-body">
          <div className="csd-close">
            <Close onClick={onClose} />
          </div>
          <h2>{localeMessage('rateSession', locale)}</h2>

          {/* Successful session radio */}
          <Controller
            name="successFeedback"
            control={control}
            render={({ field }) => (
              <RadioButton
                {...field}
                options={successLocaleOptions()}
                onChange={e => {
                  field.onChange(e);
                  handleRadioChange('successFeedback');
                }}
              />
            )}
          />

          <div className="csd-separator" />

          <p>
            <span>{localeMessage('unsuccessful', locale)}</span>
            {localeMessage('chooseReason', locale)}
          </p>

          {/* Unsuccessful session radio */}
          <Controller
            name="feedback"
            control={control}
            render={({ field }) => (
              <RadioButton
                {...field}
                options={localeOptions()}
                onChange={e => {
                  field.onChange(e);
                  handleRadioChange('feedback');
                }}
              />
            )}
          />

          <div>
            {feedbackValue && (
              <textarea
                className="ta-input"
                onChange={e => {
                  setTextAreaValue(e.target.value);
                  setRequiredError(false);
                }}
                placeholder={localeMessage('tellUsMore', locale)}
                value={textAreaValue}
              />
            )}
            {requiredError && (
              <p
                style={{
                  color: 'red',
                  width: '377px',
                  textAlign: 'left',
                  margin: '0 auto',
                }}
              >
                {localeMessage('requiredError', locale)}
              </p>
            )}
            <button
              className="white-button"
              style={{ marginTop: '16px' }}
              type="submit"
            >
              {localeMessage('completeSession', locale)}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

CompleteSessionDialog.propTypes = {
  appointment: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  submitSessionFeedback: PropTypes.func.isRequired,
  user: PropTypes.object,
  locale: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  return {
    appointment: state.appointments.appointment,
    user: state.user,
    locale: (state.user && state.user.locale) || 'en',
  };
}

export default connect(mapStateToProps, {
  submitSessionFeedback,
})(CompleteSessionDialog);
